/**
 * Extract a user-facing message from an API call failure.
 *
 * Backend errors are serialized as `{ success: false, message, code }`
 * (see backend error.middleware.ts). Axios' own `err.message` is only a generic
 * "Request failed with status code 400", so prefer the server-provided message.
 */

import axios from 'axios';

/** Backend error code sent while the caller still holds a temporary password. */
export const MUST_CHANGE_PASSWORD_CODE = 'MUST_CHANGE_PASSWORD';

/** The backend's machine-readable `code` from an API failure, if any. */
export function getErrorCode(err: unknown): string | undefined {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { code?: unknown } | undefined;
    if (data && typeof data.code === 'string' && data.code.length > 0) {
      return data.code;
    }
  }
  return undefined;
}

/**
 * True for the 403 the backend's blockIfMustChangePassword gate returns on any
 * protected route while the user's mustChangePassword flag is set.
 */
export function isMustChangePasswordError(err: unknown): boolean {
  return (
    axios.isAxiosError(err) &&
    err.response?.status === 403 &&
    getErrorCode(err) === MUST_CHANGE_PASSWORD_CODE
  );
}

/**
 * True for the 403 the backend's blockIfMfaEnrollmentRequired gate returns
 * while the company requires MFA and the user has not set it up (tracker item 63).
 */
export function isMfaEnrollmentRequiredError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 403 && getErrorCode(err) === 'MFA_ENROLLMENT_REQUIRED';
}

export function getErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: unknown } | undefined;
    if (data && typeof data.message === 'string' && data.message.length > 0) {
      return data.message;
    }
    if (err.response?.status === 403) {
      return 'You do not have permission to perform this action.';
    }
    if (err.response?.status === 404) {
      return 'Not found.';
    }
    return err.message || fallback;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}

/**
 * The machine-readable `details` of an API failure (backend BusinessError details),
 * e.g. `{ conflicts: [...] }` on a 409 RESCHEDULE_CONFLICTS; undefined when absent.
 */
export function getErrorDetails(err: unknown): Record<string, unknown> | undefined {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { details?: unknown } | undefined;
    if (data && data.details && typeof data.details === 'object' && !Array.isArray(data.details)) {
      return data.details as Record<string, unknown>;
    }
  }
  return undefined;
}
